import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import TextField from '@mui/material/TextField';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { getCsvDataTransactions } from '../../utils/paymentsServices';
import { periodReferences } from '../../utils/periodDate';
import { useDateRange } from '../../utils/useDateRange';
import { CleanFilterButton } from '../library/CompactClearFilterButton';
import { resetDataList } from '../../redux/slices/global';

import {
  updateFilterOptions,
  resetPaginationData,
  updatePeriod,
  updateProduct,
  updateColumn,
} from '../../redux/slices/transactions';
import { RootState } from '../../redux/store';
import { ExportButton } from '../library/ExportButton';
import { Messages } from '../../@types/messages';
import 'moment/locale/pt-br';
import { SelectCsatMultiple } from './SelectCsatMultiple';

moment.locale('pt-br');

const statusReferences = {
  ALL: 'Todos',
  FAIL: 'Não efetivado',
  SUCCESS: 'Efetivado',
  CANCELED: 'Cancelado',
};

const csatReferences = {
  ALL: 'Todos',
  '5': '5',
  '4': '4',
  '3': '3',
  '2': '2',
  '1': '1',
  NULL: '-',
};

const optionsValueList = {
  ALL: 'Todos',
  ALL_ITEMS: 'ALL',
};

const periodOptions = Object.values(periodReferences);

const statusOptions = ['Todos', 'Efetivado', 'Cancelado', 'Não efetivado'];

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  formControl: {
    margin: theme.spacing(1.5),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  formWarning: {
    marginLeft: theme.spacing(2),
    marginTop: '1em',
    fontSize: '0.85em',
    color: '#FF4842',
    minWidth: 120,
  },
  formButton: {
    margin: theme.spacing(0.1),
  },
}));

const columnsHeadersWithProductItem = [
  { id: 'date', label: 'Data', width: 170, align: 'left' },
  { id: 'client', label: 'Cliente', width: 250, align: 'right' },
  { id: 'cpf', label: 'CPF', width: 150, align: 'left' },
  {
    id: 'value',
    label: 'Valor',
    width: 100,
    align: 'right',
    format: (value) => value.toFixed(2),
  },
  {
    id: 'installments',
    label: 'Parcelas',
    width: 50,
    align: 'right',
  },
  {
    id: 'flag',
    label: 'Bandeira',
    width: 120,
    align: 'right',
  },
  {
    id: 'product',
    label: 'Produto',
    width: 70,
    align: 'right',
  },
  {
    id: 'status',
    label: 'Status',
    width: 50,
    align: 'right',
  },
];

const columnsHeadersOriginal = [
  { id: 'date', label: 'Data', width: 170, align: 'left' },
  { id: 'client', label: 'Cliente', width: 250, align: 'right' },
  { id: 'cpf', label: 'CPF', width: 150, align: 'left' },
  {
    id: 'value',
    label: 'Valor',
    width: 100,
    align: 'right',
    format: (value) => value.toFixed(2),
  },
  {
    id: 'installments',
    label: 'Parcelas',
    width: 50,
    align: 'right',
  },
  {
    id: 'flag',
    label: 'Bandeira',
    width: 120,
    align: 'right',
  },
  {
    id: 'status',
    label: 'Status',
    width: 50,
    align: 'right',
  },
  {
    id: 'place',
    label: 'Estabelecimento',
    width: 50,
    align: 'right',
  },
];

export function TransactionsFormFilter() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { switchOptions, _order, period, status, flag, product, csat, authorizersPlace, listProducts } = useSelector(
    (state: RootState) => state.transactions
  );

  const flagReferences = {
    ALL: 'Todas',
    ...authorizersPlace,
  };
  const getSwitches = () => Object.values(flagReferences);
  const { reset } = useSelector((state: RootState) => state.global);

  let initialState = {
    period,
    status,
    flag,
    product,
    csat,
    client: '',
    place: '',
    nsu: '',
    userInfo: '',
  };

  const [filter, setFilter] = useState(initialState);
  const [privateLabelProduct, setPrivateLabelProduct] = useState<any>();
  const [privateLabelItem, setPrivateLabelItem] = useState<any>('Todos');
  const [exportRequesting, setExportRequesting] = useState(false);
  const [placeSelected, setPlaceSelected] = useState<any>('Todos');
  const [groupPlaces, setGroupPlaces] = useState<any>([]);
  const [nsu, setNsu] = useState('');
  const [userInfo, setUserInfo] = useState('');
  const { valueFromDate, valueBeforeDate, periodDateRange, handleDateChange } = useDateRange();

  useEffect(() => {
    const selectedGroupPlaces = localStorage?.getItem('group-selected-places-id')?.length
      ? JSON.parse(localStorage.getItem('group-selected-places-id')!)
      : [];
    localStorage.setItem('selected-places-id', JSON.stringify(selectedGroupPlaces));
    setGroupPlaces(selectedGroupPlaces);
  }, []);

  useEffect(() => {
    if (periodDateRange) {
      dispatch(updatePeriod(periodDateRange));
    }
  }, [periodDateRange, dispatch]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'period') {
      const periodValues = Object.values(periodReferences);
      const periodKeys = Object.keys(periodReferences);
      const foundIndexOf = periodValues.findIndex((item) => item === value);
      setNsu('');
      setUserInfo('');
      const newFilter = { ...filter, [name]: periodKeys[foundIndexOf], nsu: '', userInfo: '' };
      setFilter(newFilter);
      if (periodKeys[foundIndexOf] === 'BETWEEN_DATES') return;
      dispatch({ type: 'transactions/getReportSuccess', payload: null });
      dispatch(updateFilterOptions(newFilter));
    } else if (name === 'status') {
      const statusValues = Object.values(statusReferences);
      const statusKeys = Object.keys(statusReferences);
      const foundIndexOf = statusValues.findIndex((item) => item === value);
      setNsu('');
      setUserInfo('');
      const newFilter = { ...filter, [name]: statusKeys[foundIndexOf], nsu: '', userInfo: '' };
      setFilter(newFilter);
      dispatch(updateFilterOptions(newFilter));
    } else if (name === 'flag') {
      dispatch(updateColumn(columnsHeadersOriginal));
      const flagValues = Object.values(flagReferences);
      const flagKeys = Object.keys(flagReferences);
      const foundIndexOf = flagValues.findIndex((item) => item === value);
      const authorizerItem = switchOptions.find((item) => item.name === value);
      const isPrivateLabel = authorizerItem?.isPrivateLabel;
      setNsu('');
      setUserInfo('');
      setPrivateLabelProduct(isPrivateLabel ? listProducts : null);
      dispatch(updateProduct(optionsValueList.ALL_ITEMS));
      if (isPrivateLabel) {
        setNsu('');
        setUserInfo('');
        setPrivateLabelItem(optionsValueList.ALL_ITEMS);
        dispatch(updateColumn(columnsHeadersWithProductItem));
      }
      const newFilter = {
        ...filter,
        [name]: flagKeys[foundIndexOf],
        product: optionsValueList.ALL_ITEMS,
        nsu: '',
        userInfo: '',
      };
      setFilter(newFilter);
      dispatch(updateFilterOptions(newFilter));
    } else if (name === 'csat') {
      const csatValues = Object.values(csatReferences);
      const csatKeys = Object.keys(csatReferences);
      const foundIndexOf = csatValues.findIndex((item) => item === value);
      const csatKey = csatKeys[foundIndexOf];
      const csatValue = csatKey;
      setNsu('');
      setUserInfo('');
      const newFilter = { ...filter, [name]: csatValue, nsu: '', userInfo: '' };
      setFilter(newFilter);
      dispatch(updateFilterOptions(newFilter));
      dispatch(resetPaginationData(true));
      void dispatch(resetDataList(true));
    } else if (name === 'product') {
      const valueFormated = value === optionsValueList.ALL ? optionsValueList.ALL_ITEMS : value;
      setPrivateLabelItem(valueFormated);
      setNsu('');
      setUserInfo('');
      const newFilter = { ...filter, [valueFormated]: value, nsu: '', userInfo: '' };
      setFilter(newFilter);
      dispatch(updateFilterOptions(newFilter));
    } else if (name === 'nsu') {
      setNsu(value);
      setFilter({ ...filter, [name]: value || '' });
    } else if (name === 'userInfo') {
      setUserInfo(value);
      setFilter({ ...filter, [name]: value || '' });
    } else if (name === 'place') {
      setPlaceSelected(value);
      if (value === 'Todos') localStorage.setItem('selected-places-id', JSON.stringify(groupPlaces));
      else localStorage.setItem('selected-places-id', JSON.stringify([value]));
      setNsu('');
      setUserInfo('');
      setFilter({ ...filter, [name]: value || undefined });
      dispatch(
        updateFilterOptions({
          period: 'ALL',
          status: 'ALL',
          flag: 'ALL',
          product: 'ALL',
          client: '',
          nsu: '',
          userInfo: '',
        })
      );
      void dispatch(resetDataList(true));
      dispatch(resetPaginationData(true));
    }
  };

  const handleCleanFilter = () => {
    initialState = {
      period: 'ALL',
      status: 'ALL',
      flag: 'ALL',
      product: '',
      csat: 'ALL',
      client: '',
      place: 'Todos',
      nsu: '',
      userInfo: '',
    };
    setNsu('');
    setUserInfo('');
    setFilter(initialState);
    dispatch(updateFilterOptions(initialState));
  };

  useEffect(() => {
    setPrivateLabelProduct(listProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listProducts]);

  const renderList = (list) =>
    list.map((item, index) => (
      <MenuItem key={index} value={item}>
        {item}
      </MenuItem>
    ));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (nsu || userInfo) {
      handleSearch(event);
    }
    setNsu('');
    setUserInfo('');
    dispatch(resetPaginationData(true));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    dispatch(resetPaginationData(true));
    const filterOptions = { ...filter };
    if (!filterOptions.nsu) {
      filterOptions.nsu = '';
    }
    if (!filterOptions.userInfo) {
      filterOptions.userInfo = '';
    }
    dispatch(updateFilterOptions(filterOptions));
  };

  const handleExport = async () => {
    setExportRequesting(true);
    const end = Number.MAX_SAFE_INTEGER;
    const start = 0;
    const sort = 'createdAt';
    try {
      const result = await getCsvDataTransactions({
        _end: end,
        _order,
        _sort: sort,
        _start: start,
        period,
        authorizer: flag,
        status,
        csat,
      });
      setExportRequesting(false);
      return result;
    } catch (error: any) {
      enqueueSnackbar(Messages[error.message], {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
      setExportRequesting(false);
      return undefined;
    }
  };

  useEffect(() => {
    if (reset === true) {
      setFilter(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  useEffect(() => {
    setFilter({ ...filter, status });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    setFilter({ ...filter, flag });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag]);

  useEffect(() => {
    setFilter({ ...filter, product });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  return (
    <Box
      sx={{ mb: 1, mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <form
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
        id="paymentsForm"
        onSubmit={(event) => handleSubmit(event)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="payments-period">Período</InputLabel>
            <Select
              labelId="period-label"
              id="period"
              name="period"
              value={periodReferences[filter.period]}
              onChange={(event) => handleChange(event)}
              label="Período"
            >
              {renderList(periodOptions)}
            </Select>
          </FormControl>

          {filter.period === 'BETWEEN_DATES' && (
            <>
              <FormControl variant="outlined" className={classes.formControl}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="De"
                    inputFormat="DD/MM/YYYY"
                    value={valueFromDate?.toDate() || null}
                    onChange={(newDate) => handleDateChange(newDate ? moment(newDate) : null, true)}
                    renderInput={(params) => <TextField {...params} sx={{ width: '170px' }} />}
                  />
                </LocalizationProvider>
              </FormControl>
              <FormControl variant="outlined" className={classes.formControl}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Até"
                    inputFormat="DD/MM/YYYY"
                    value={valueBeforeDate?.toDate() || null}
                    onChange={(newDate) => handleDateChange(newDate ? moment(newDate) : null, false)}
                    renderInput={(params) => <TextField {...params} sx={{ width: '170px' }} />}
                  />
                </LocalizationProvider>
              </FormControl>
            </>
          )}
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="payments-status">Status</InputLabel>
            <Select
              labelId="status-label"
              id="status"
              name="status"
              value={statusReferences[filter.status]}
              onChange={(event) => handleChange(event)}
              label="Status"
            >
              {renderList(statusOptions)}
            </Select>
          </FormControl>
          <SelectCsatMultiple filter={filter} setNsu={setNsu} />
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="payments-flag">Bandeira</InputLabel>
            <Select
              labelId="flag-label"
              id="flag"
              name="flag"
              value={flagReferences[filter.flag]}
              onChange={(event) => handleChange(event)}
              label="Bandeira"
            >
              {renderList([...getSwitches()])}
            </Select>
          </FormControl>
          {privateLabelProduct && (
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="payments-products">Produtos</InputLabel>
              <Select
                labelId="products-label"
                id="product"
                name="product"
                value={privateLabelItem === 'ALL' ? 'Todos' : privateLabelItem}
                onChange={(event) => handleChange(event)}
                label="Produto"
              >
                {renderList(['Todos', ...privateLabelProduct])}
              </Select>
            </FormControl>
          )}
          <CleanFilterButton onClick={handleCleanFilter} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl style={{ flexDirection: 'row' }}>
              <TextField
                id="nsu"
                name="nsu"
                className={classes.formControl}
                onChange={(event) => handleChange(event)}
                style={{ minWidth: '110px' }}
                required={false}
                value={nsu}
                label="NSU"
              />
              <TextField
                id="userInfo"
                name="userInfo"
                className={classes.formControl}
                onChange={(event) => handleChange(event)}
                style={{ minWidth: '110px' }}
                required={false}
                value={userInfo}
                label="Nome/CPF"
              />
            </FormControl>
            <LoadingButton
              color="primary"
              variant="outlined"
              size="medium"
              onClick={handleSearch}
              className={classes.formButton}
              disabled={!nsu && !userInfo}
            >
              Buscar
            </LoadingButton>
          </Box>
          <div hidden>
            <FormControl variant="outlined" className={classes.formControl} hidden>
              <InputLabel id="payments-places">Estabelecimento</InputLabel>
              <Select
                labelId="place-label"
                id="place"
                name="place"
                value={placeSelected}
                onChange={(event) => handleChange(event)}
                label="Estabelecimento"
              >
                {renderList(['Todos', ...groupPlaces])}
              </Select>
            </FormControl>
          </div>
        </Box>
        <Box
          sx={{
            display: 'flex',
            ...(privateLabelProduct && filter.period === 'BETWEEN_DATES' ? { marginLeft: 2 } : { marginRight: 2 }),
          }}
        >
          <ExportButton
            handleExport={handleExport}
            isRequesting={exportRequesting}
            name={Messages.TRANSACTION_FILE_NAME}
          />
        </Box>
      </form>
      <div className={classes.formWarning}>
        Atenção! As informações de data são baseadas no Fuso horário de Brasília, DF (GMT-3).
      </div>
    </Box>
  );
}
